<script setup>
/**
 * What WhatsApp says about one piece of wording — and nothing more than that.
 *
 * THIS COMPONENT IS THE FEATURE'S LOAD-BEARING HONESTY, so the rules are worth writing down.
 *
 * Chatwoot has fabricated a template's status before. On the Twilio list path it derived
 * `category` from a regex over the template's NAME and answered `approved` for everything; the
 * fix was to read Twilio's `content_and_approvals` instead of its `contents`. On the WhatsApp
 * Cloud path it still hardcodes `PENDING` in two places after a create
 * (`csat_template_service.rb:113`, `inbox_csat_templates_controller.rb:96`) without looking at
 * what Meta answered. Both defects look identical to an owner: a confident word that is not a
 * fact. She then builds a broadcast on wording Meta has not approved, and the send fails per
 * recipient, after she has paid for the first ones.
 *
 * So:
 *   • `status` is nullable, and NULL IS A STATE WITH ITS OWN WORD — "WhatsApp hasn't said" —
 *     never an assumed PENDING and never a blank chip.
 *   • A status we do not recognise is shown as the word META sent, in amber, rather than being
 *     folded into the nearest one we know. Meta's own two reference pages disagree about this
 *     enum today (`ARCHIVED` is on one and missing from the other) and its webhook enum carries
 *     `FLAGGED`, `LOCKED` and `REINSTATED`, which Meta defines nowhere. An unmapped value folded
 *     into "waiting" is the same lie in a new coat. `<bdi>` isolates it, because it is foreign
 *     text of unknown direction sitting in an Arabic sentence.
 *   • The colours are about what an owner must DO, not about tidiness: only an approved template
 *     can be sent. `PENDING` is slate — nothing to do but wait. Everything that needs her
 *     attention is amber or ruby.
 *
 * ── THE QUALITY RATING IS NOT IN HERE, AND THAT IS THE FIX ──────────────────────────
 *
 * It was. This chip read `quality`, composed "Approved · low quality", and took its COLOUR from
 * the rating rather than the status — so an APPROVED template rated low rendered the same ruby
 * as a REFUSED one. On a screen with fourteen verdicts, no legend anywhere and colour as the
 * only signal, that is a wrong-answer generator: the two facts an owner most needs to keep
 * apart were drawn identically.
 *
 * The rating now has its own chip, `TemplateQualityChip`, rendered beside this one. This chip
 * says ONE thing — what WhatsApp has decided about the wording — and approved is blue, always.
 */
import { computed } from 'vue';

import { statusOf } from '../helpers/template-facts.js';
import { useWasloI18n } from '../i18n.js';

const props = defineProps({
  /** Meta's own word, in Meta's own casing, or null when the row carries none. */
  status: { type: String, default: null },
});

const { t } = useWasloI18n();

const TONE = {
  APPROVED: 'bg-n-blue-3 text-n-blue-11',
  PENDING: 'bg-n-slate-3 text-n-slate-11',
  IN_APPEAL: 'bg-n-amber-3 text-n-amber-11',
  PAUSED: 'bg-n-amber-3 text-n-amber-11',
  PENDING_DELETION: 'bg-n-amber-3 text-n-amber-11',
  ARCHIVED: 'bg-n-amber-3 text-n-amber-11',
  REJECTED: 'bg-n-ruby-3 text-n-ruby-11',
  DISABLED: 'bg-n-ruby-3 text-n-ruby-11',
  LIMIT_EXCEEDED: 'bg-n-ruby-3 text-n-ruby-11',
  DELETED: 'bg-n-slate-3 text-n-slate-11',
};

const UNTOLD = 'bg-n-amber-3 text-n-amber-11';

const folded = computed(() => statusOf({ status: props.status }));

const known = computed(() => Object.keys(TONE).includes(folded.value));

const tone = computed(() => TONE[folded.value] ?? UNTOLD);

/** Empty for the one case that renders Meta's own word instead of one of ours. */
const label = computed(() => {
  if (!folded.value) return t('WASLO.TEMPLATES.STATUS.UNTOLD');
  if (!known.value) return '';
  return t(`WASLO.TEMPLATES.STATUS.${folded.value}`);
});
</script>

<template>
  <span
    class="inline-flex items-center h-6 px-2 text-xs font-medium rounded-md shrink-0"
    :class="tone"
    :data-waslo-template-status="folded || 'untold'"
  >
    <template v-if="label">{{ label }}</template>
    <bdi v-else>{{ status }}</bdi>
  </span>
</template>
